'use client';

import { createClient } from '@/lib/supabase/client';
import { emailService } from '@/lib/services/emailService';

function isSchemaError(error: any): boolean {
  if (!error) return false;
  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') return true;
  }
  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
    ];
    return schemaErrorPatterns.some((pattern) => pattern.test(error.message));
  }
  return false;
}

export interface QuoteRequest {
  fullName: string;
  email: string;
  phone: string;
  businessType: string;
  containerSize: string;
  location: string;
  timeline: string;
  message?: string;
}

export const quoteService = {
  async createQuote(quoteData: QuoteRequest): Promise<boolean> {
    const supabase = createClient();

    try {
      // Get current user if authenticated
      const {
        data: { user },
      } = await supabase.auth.getUser();

      // Convert business type to enum format
      const businessTypeEnum = quoteData.businessType
        .toLowerCase()
        .replace(/ /g, '_');

      // Convert timeline to enum format with proper mapping
      let timelineEnum: string;
      switch (quoteData.timeline.toLowerCase()) {
        case 'as soon as possible':
          timelineEnum = 'asap';
          break;
        case 'within 1-2 months':
          timelineEnum = 'one_to_two_months';
          break;
        case 'within 3-6 months':
          timelineEnum = 'three_to_six_months';
          break;
        case 'just exploring options':
          timelineEnum = 'exploring';
          break;
        default:
          timelineEnum = 'exploring';
      }

      // Insert quote
      const { error: quoteError } = await supabase.from('quotes').insert({
        user_id: user?.id || null,
        full_name: quoteData.fullName,
        email: quoteData.email,
        phone: quoteData.phone,
        business_type: businessTypeEnum,
        container_size: quoteData.containerSize,
        location: quoteData.location,
        timeline: timelineEnum,
        message: quoteData.message || null,
        status: 'pending',
      });

      if (quoteError) {
        if (isSchemaError(quoteError)) {
          console.error('Schema error:', quoteError.message);
          throw quoteError;
        } else {
          console.log('Quote creation error:', quoteError.message);
          return false;
        }
      }

      // Create lead entry for tracking
      const { error: leadError } = await supabase.from('leads').insert({
        email: quoteData.email,
        phone: quoteData.phone,
        full_name: quoteData.fullName,
        business_type: businessTypeEnum,
        source: 'quote_form',
        status: 'new',
      });

      // Lead creation is optional, don't fail if it errors
      if (leadError && isSchemaError(leadError)) {
        console.error('Lead creation schema error:', leadError.message);
      } else if (!leadError) {
        // Send admin notification for new lead
        const adminDashboardUrl = `${typeof window !== 'undefined' ? window.location.origin : ''}/admin/leads`;
        await emailService.sendAdminLeadNotification({
          adminEmail: 'admin@containersbyzip.com',fullName: quoteData.fullName,email: quoteData.email,phone: quoteData.phone,source: 'quote_form',
          businessType: businessTypeEnum,
          adminDashboardUrl,
        });
      }

      return true;
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  async getUserQuotes(): Promise<any[]> {
    const supabase = createClient();

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from('quotes').select('*').eq('user_id', user.id).order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return [];
        }
      }

      return data || [];
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },
};